<script setup lang="ts">
import type { Form } from '#ui/types';
import { z } from 'zod';

import { apiAccountExist, BackendErrorCode } from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { AuthGuardMode } from '~/types/authGuard';

definePageMeta({
    layout: false,
    middleware: 'auth-guard',
    authGuard: { mode: AuthGuardMode.GUEST_ONLY },
});

// COMPOSABLES
const { tt } = useTypedI18n();
const route = useRoute();
const { isLgOrGreater } = useResponsive();
const { login } = useAuthUtils();
const { openRequestPasswordResetModal } = useModals();
const { notifyBackendError, notifyGenericError } = useNotification();
const localePath = useLocalePath();

// DATA
const formSchema = z.object({
    email: zodRequiredString(tt('validation.msg.required', { field: tt('login.email') })).email(
        tt('validation.msg.invalid', { field: tt('login.email') })
    ),
    password: z
        .string()
        .refine(password => !isPromptingPassword.value || !!password, `${tt('validation.msg.required', { field: tt('login.password') })}`),
});

type FormSchema = typeof formSchema;
type FormData = z.infer<FormSchema>;

const formData = reactive<FormData>({
    email: locationQueryAsOptionalString(route.query.email) ?? '',
    password: '',
});

const form = useTemplateRef<Form<FormSchema>>('form');
const isSubmitting = ref(false);
const isPromptingPassword = ref(false);

// COMPUTED
const submitLabel = computed(() => tt(isPromptingPassword.value ? 'login.loginButton' : 'login.nextButton'));

// METHODS
const goBack = (): void => {
    navigateTo(locationQueryAsOptionalString(route.query.redirect) ?? localePath('index'));
};

const accountCheck = (): Promise<void> =>
    apiAccountExist({ email: formData.email })
        .then(() => {
            isPromptingPassword.value = true;
        })
        .catch(error => {
            if (isFetchError(error) && isBackendError(error.data) && error.data.error.code === BackendErrorCode.EntityNotFound) {
                navigateTo(localePath({ name: ROUTE_NAMES.SIGNUP, query: { ...route.query, email: formData.email } }));
            } else {
                return Promise.reject(error);
            }
        });

const accountLogin = (): Promise<void | Record<string, any> | undefined> =>
    login(formData.email, formData.password).catch(error => {
        if (isFetchError(error) && isBackendError(error.data) && error.data.error.code === BackendErrorCode.AuthWrongPassword) {
            form.value?.setErrors([{ name: 'password', message: tt('error.backend.AuthWrongPassword') }]);
        } else {
            return Promise.reject(error);
        }
    });

const onSubmit = async (): Promise<void> => {
    isSubmitting.value = true;

    try {
        await (isPromptingPassword.value ? accountLogin : accountCheck)();
    } catch (error) {
        if (isFetchError(error) && isBackendError(error.data)) {
            notifyBackendError({ error: error.data.error });
        } else {
            notifyGenericError();
        }
    }

    isSubmitting.value = false;
};

const onForgotPasswordClick = (): void => {
    openRequestPasswordResetModal({ email: formData.email });
};
</script>

<template>
    <div>
        <NuxtLayout name="side-image" bg-src="/block/hero/blue-bus.webp">
            <div class="flex h-full w-full flex-col justify-between gap-12">
                <SideImageHeader @back="goBack" />

                <LoginGatedActionInfo />

                <UForm
                    ref="form"
                    class="flex flex-col justify-center gap-6"
                    :state="formData"
                    :schema="formSchema"
                    :validate-on="['input', 'change']"
                    @submit="onSubmit"
                >
                    <h1 class="text-highlighted text-center text-2xl font-bold">{{ tt('login.title') }}</h1>
                    <p class="text-muted text-center font-medium">{{ tt('login.subtitle') }}</p>

                    <template v-if="!isPromptingPassword">
                        <UFormField class="mb-2 w-full" :label="tt('login.email')" name="email" required>
                            <UInput
                                v-model.trim="formData.email"
                                autocomplete="username"
                                type="email"
                                inputmode="email"
                                :disabled="isSubmitting"
                                :autofocus="isLgOrGreater"
                            />
                        </UFormField>
                    </template>

                    <template v-else>
                        <UFormField class="mb-2 w-full" :label="tt('login.email')" name="email" required>
                            <UInput :model-value="formData.email" disabled :ui="{ base: 'disabled:bg-neutral-100' }">
                                <template #trailing>
                                    <span class="text-primary cursor-pointer text-sm font-bold" @click="isPromptingPassword = false">Cambia</span>
                                </template>
                            </UInput>
                        </UFormField>

                        <UFormField v-if="isPromptingPassword" class="w-full" :label="tt('login.password')" name="password" required>
                            <BasePasswordInput v-model="formData.password" autocomplete="current-password" :disabled="isSubmitting" />
                        </UFormField>

                        <button
                            v-if="isPromptingPassword"
                            type="button"
                            class="mx-auto text-base font-bold underline"
                            @click.prevent="onForgotPasswordClick"
                        >
                            {{ tt('login.forgotPassword') }}
                        </button>
                    </template>

                    <UButton block :label="submitLabel" type="submit" size="xl" :loading="isSubmitting" />
                </UForm>

                <SideImageFooter />
            </div>
        </NuxtLayout>
    </div>
</template>
